import React, { useState } from "react";
import { Copy, Check, Landmark, CreditCard } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { usd, CARD_PAYMENTS_ENABLED } from "../../lib/api";

export const inputCls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm focus:border-[#008057] outline-none bg-white";
const boxCls = "rounded-2xl bg-white border border-[#E2EBF0] p-6";

const NOMINEE_FIELDS = [
  ["first_name", "Child's first name"], ["last_name", "Child's last name"],
  ["age", "Child's age or date of birth (if known)"], ["guardian_name", "Guardian's name (if known)"],
  ["guardian_contact", "Guardian's contact (if known)"], ["notes", "Additional info (optional)"],
];

function ModeOption({ active, onSelect, children }) {
  return (
    <label className={`rounded-xl border-2 px-3 py-2 text-sm cursor-pointer ${active ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0]"}`}>
      <input type="radio" className="accent-[#008057] mr-2" checked={active} onChange={onSelect} />{children}
    </label>
  );
}

function BeneficiaryCard({ a, i, setAlloc, setNominee }) {
  return (
    <div data-testid={`beneficiary-card-${i}`} className="rounded-xl border border-[#E2EBF0] p-4">
      <div className="font-semibold text-[#03281B] mb-2">Sponsorship #{i + 1}</div>
      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        <ModeOption active={a.mode === "organization"} onSelect={() => setAlloc({ mode: "organization" })}>Let the organization select a child</ModeOption>
        <ModeOption active={a.mode === "nominate"} onSelect={() => setAlloc({ mode: "nominate" })}>I would like to nominate a child</ModeOption>
      </div>
      {a.mode === "organization" ? (
        <p className="text-xs text-slate-500">The organization will select an eligible child in accordance with the official sponsorship program regulations, prioritizing young players who would otherwise be unable to participate.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {NOMINEE_FIELDS.map(([k, ph]) => <input key={k} className={inputCls} placeholder={ph} value={a.nominee[k] || ""} onChange={(e) => setNominee({ [k]: e.target.value })} />)}
          <p className="text-xs text-slate-500 sm:col-span-2">Nomination is subject to eligibility verification and approval by the organization; it does not automatically confirm the child's registration. Only the parent or authorized legal guardian may complete the child's formal registration.</p>
        </div>
      )}
    </div>
  );
}

export function BeneficiaryList({ allocations, setAllocations }) {
  const patch = (i, fn) => setAllocations((prev) => prev.map((a, idx) => (idx === i ? fn(a) : a)));
  return (
    <div className={boxCls}>
      <div className="font-display font-bold text-lg text-[#03281B] mb-1">Beneficiary selection</div>
      <p className="text-sm text-slate-500 mb-4">For each sponsorship, nominate a specific child or let the organization select an eligible child.</p>
      <div className="space-y-4">
        {allocations.map((a, i) => (
          <BeneficiaryCard key={a._k} a={a} i={i}
            setAlloc={(p) => patch(i, (x) => ({ ...x, ...p }))}
            setNominee={(p) => patch(i, (x) => ({ ...x, nominee: { ...x.nominee, ...p } }))} />
        ))}
      </div>
    </div>
  );
}

const INDIVIDUAL_FIELDS = [["first_name", "First name", "sponsor-fname"], ["last_name", "Last name"], ["email", "Email address", "sponsor-email"], ["phone", "Phone number"]];
const CORPORATE_FIELDS = [["company", "Company / organization name"], ["contact_person", "Contact person's name"], ["business_email", "Business email", "sponsor-email"], ["business_phone", "Business phone"], ["billing_address", "Billing address (if required)", null, true]];

export function SponsorDetailsForm({ type, setType, sponsor, setSponsor, ackName, setAckName, anonymous, setAnonymous }) {
  const fields = type === "individual" ? INDIVIDUAL_FIELDS : CORPORATE_FIELDS;
  return (
    <div className={boxCls}>
      <div className="font-display font-bold text-lg text-[#03281B] mb-3">Your details</div>
      <div className="flex gap-2 mb-4">
        {["individual", "corporate"].map((t) => (
          <button key={t} onClick={() => setType(t)} className={`flex-1 rounded-xl border-2 py-2 text-sm font-semibold capitalize ${type === t ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0]"}`}>{t === "individual" ? "Individual" : "Corporate / Organization"}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map(([k, ph, tid, wide]) => (
          <input key={`${type}-${k}`} data-testid={tid || undefined} className={`${inputCls} ${wide ? "sm:col-span-2" : ""}`} placeholder={ph} value={sponsor[k] || ""} onChange={(e) => setSponsor({ ...sponsor, [k]: e.target.value })} />
        ))}
        <input className={`${inputCls} sm:col-span-2`} placeholder="Name for public acknowledgment (optional)" value={ackName} onChange={(e) => setAckName(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 mt-4 text-sm text-slate-700 cursor-pointer">
        <input data-testid="sponsor-anonymous" type="checkbox" className="accent-[#008057] w-4 h-4" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
        I would like my sponsorship to remain anonymous.
      </label>
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); };
  return <button onClick={copy} className="text-[#008057]">{copied ? <Check size={14} /> : <Copy size={14} />}</button>;
}

function SponsorBankDetails({ bank }) {
  const rows = [["Bank", bank.bank], ["Account holder", bank.account_holder], ["Account number", bank.account_number], ["ACH routing", bank.routing_ach], ["Wire routing", bank.routing_wire]];
  return (
    <div className="mt-4 rounded-xl bg-[#F2F8F5] p-4 text-sm space-y-1">
      {rows.map(([l, v]) => (
        <div key={l} className="flex items-center justify-between"><span className="text-slate-500">{l}: <b className="text-[#03281B] font-mono">{v}</b></span><CopyBtn value={v} /></div>
      ))}
      <p className="text-xs text-slate-500 pt-1">A unique reference is generated; include it in your transfer. Sponsorship stays Pending Payment until we verify funds.</p>
      {bank.zelle?.handle && (
        <div className="mt-2 pt-3 border-t border-[#008057]/15">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Zelle / Chase QuickPay <span className="text-[#F3C242]">· fastest</span></div>
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-lg border border-slate-200 shrink-0"><QRCodeCanvas data-testid="sponsor-zelle-qr" value={bank.zelle.handle} size={82} fgColor="#03281B" /></div>
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Zelle: <b className="text-[#03281B] font-mono">{bank.zelle.display || bank.zelle.handle}</b></span><CopyBtn value={bank.zelle.handle} /></div>
              {bank.zelle.name && <div className="text-xs text-slate-500">Recipient: {bank.zelle.name}</div>}
              <p className="text-[11px] text-slate-400 mt-0.5">Scan or send Zelle to this number; add your reference in the memo.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SponsorPayment({ method, setMethod, bank }) {
  const btn = (active) => `rounded-xl border-2 p-4 text-left ${active ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0]"}`;
  return (
    <div className={boxCls}>
      <div className="font-display font-bold text-lg text-[#03281B] mb-3">Payment method</div>
      <div className={`grid ${CARD_PAYMENTS_ENABLED ? "sm:grid-cols-2" : "grid-cols-1"} gap-3`}>
        {CARD_PAYMENTS_ENABLED && (
          <button data-testid="sponsor-pay-stripe" onClick={() => setMethod("stripe")} className={btn(method === "stripe")}><CreditCard className="text-[#008057]" /><div className="font-bold mt-2">Credit Card</div><div className="text-xs text-slate-500">Secure via Stripe</div></button>
        )}
        <button data-testid="sponsor-pay-bank" onClick={() => setMethod("bank")} className={btn(method === "bank")}><Landmark className="text-[#008057]" /><div className="font-bold mt-2">Bank Transfer</div><div className="text-xs text-slate-500">ACH or wire</div></button>
      </div>
      {method === "bank" && <SponsorBankDetails bank={bank} />}
    </div>
  );
}

export function SponsorSummary({ pkg, qty, total }) {
  return (
    <div className="rounded-2xl bg-[#041E14] text-white p-6 lg:sticky lg:top-20">
      <div className="font-display font-bold text-lg">Sponsorship Summary</div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-white/60">Package</span><span data-testid="sponsor-summary-package">{pkg?.name}</span></div>
        <div className="flex justify-between"><span className="text-white/60">Children</span><span data-testid="sponsor-qty">{qty}</span></div>
        <div className="flex justify-between text-[#F3C242] font-display font-extrabold text-2xl pt-2 border-t border-white/10 mt-2"><span>Total</span><span data-testid="sponsor-total">{usd(total)}</span></div>
      </div>
      <p className="text-white/50 text-xs mt-4">Includes a full camp scholarship (registration, official Sporting CP scarf, insurance and training kit) for each sponsored child. Optional services are not included.</p>
    </div>
  );
}
